/**
 * Centralized route configuration for the CRM application.
 * Single source of truth for paths, permissions, and route definitions.
 */
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import ProtectedRoute from '../components/ProtectedRoute';
import Login from '../components/Login/Login';
import Register from '../components/Register';
import Dashboard from '../pages/Dashboard.jsx';
import CreateMerchant from '../pages/CreateMerchant';
import InUnderwriting from '../pages/InUnderwriting';
import Pending from '../pages/Pending';
import Complete from '../pages/Complete';
import ViewMerchant from '../pages/ViewMerchant';
import ProfilePage from '../pages/ProfilePage.jsx';
import UserCreatePage from '../pages/UserCreatePage.jsx';
import UserListPage from '../pages/UserListPage.jsx';
import CustomerList from '../pages/customers/CustomerList.jsx';
import CreateCustomer from '../pages/customers/CreateCustomer.jsx';
import { CUSTOMER_LIST_ACCESS, CUSTOMER_VIEW_ACCESS, CUSTOMER_PERMISSIONS } from '../pages/customers/customerPermissions.js';
import TeamsListPage from '../pages/TeamsListPage.jsx';
import CreateTeamPage from '../pages/CreateTeamPage.jsx';
import ViewLogsPage from '../pages/ViewLogsPage.jsx';
import LeadList from '../pages/Leads/LeadList.jsx';
import CreateLead from '../pages/Leads/CreateLead.jsx';
import MeetingCalendar from '../pages/meetings/MeetingCalendar.jsx';
import MeetingView from '../pages/meetings/MeetingView.jsx';
import CreateMeeting from '../pages/meetings/CreateMeeting.jsx';
import EditMeeting from '../pages/meetings/EditMeeting.jsx';
import SalesDbaListing from '../pages/SalesDbaListing.jsx';
import LeadDetail from '../pages/Leads/LeadDetail.jsx';
import LeadUploadDocuments from '../pages/Leads/LeadUploadDocuments.jsx';
import PermissionAssignment from '../pages/permission/PermissionAssignment.jsx';
import RolesManagement from '../pages/permission/RolesManagement.jsx';
import RoleList from '../pages/roles/RoleList.jsx';
import CreateRole from '../pages/roles/CreateRole.jsx';
import StatusList from '../pages/statuses/StatusList.jsx';
import CreateStatus from '../pages/statuses/CreateStatus.jsx';
import IssueList from '../pages/Issues/IssueList.jsx';
import IssueDetail from '../pages/Issues/IssueDetail.jsx';
import MerchantList from '../pages/Merchants/MerchantsList.jsx';
import Equipemnts from '../pages/Merchants/Equipemnts.jsx';
import ContractList from '../pages/contracts/ContractList.jsx';
import DailySummaryList from '../pages/dailySummary/DailySummaryList.jsx';
import CreateDailySummary from '../pages/dailySummary/CreateDailySummary.jsx';
import EditDailySummary from '../pages/dailySummary/EditDailySummary.jsx';
import ViewDailySummary from '../pages/dailySummary/ViewDailySummary.jsx';
import CallsCounterSummary from '../pages/CallsCounterSummary.jsx';
import LeadsReport from '../pages/LeadsReport.jsx';
import RecentActivitiesReport from '../pages/reports/RecentActivitiesReport.jsx';
import NotificationsPage from '../pages/NotificationsPage.jsx';
import CallRecordList from '../pages/callRecords/CallRecordList.jsx';
import CreateCallRecord from '../pages/callRecords/CreateCallRecord.jsx';
import ViewCallRecord from '../pages/callRecords/ViewCallRecord.jsx';
import EditCallRecord from '../pages/callRecords/EditCallRecord.jsx';
import { LIST_ROUTE_ANY_PERMISSIONS } from './resourceListPermissions.js';
import CompanyList from '../pages/companies/CompanyList.jsx';
import CreateCompany from '../pages/companies/CreateCompany.jsx';
import ViewCompany from '../pages/companies/ViewCompany.jsx';
import TaskTypeList from '../pages/taskTypes/TaskTypeList.jsx';
import CreateTaskType from '../pages/taskTypes/CreateTaskType.jsx';
import ViewTaskType from '../pages/taskTypes/ViewTaskType.jsx';
import TaskCategoryList from '../pages/taskCategories/TaskCategoryList.jsx';
import CreateTaskCategory from '../pages/taskCategories/CreateTaskCategory.jsx';
import ViewTaskCategory from '../pages/taskCategories/ViewTaskCategory.jsx';
import TaskTemplateList from '../pages/taskTemplates/TaskTemplateList.jsx';
import CreateTaskTemplate from '../pages/taskTemplates/CreateTaskTemplate.jsx';
import ViewTaskTemplate from '../pages/taskTemplates/ViewTaskTemplate.jsx';
import TaskList from '../pages/tasks/TaskList.jsx';
import CreateTask from '../pages/tasks/CreateTask.jsx';
import ViewTask from '../pages/tasks/ViewTask.jsx';
import CreateBrand from '../pages/brands/CreateBrand.jsx';
import ViewBrand from '../pages/brands/ViewBrand.jsx';
import BrandList from '../pages/brands/BrandList.jsx';
import CreateService from '../pages/services/CreateService.jsx';
import ViewService from '../pages/services/ViewService.jsx';
import ServicesList from '../pages/services/ServicesList.jsx';
import LeaseList from '../pages/leases/LeaseList.jsx';
import CreateLease from '../pages/leases/CreateLease.jsx';
import ViewLease from '../pages/leases/ViewLease.jsx';
import MerchantServiceProviderList from '../pages/merchantServiceProviders/MerchantServiceProviderList.jsx';
import CreateMerchantServiceProvider from '../pages/merchantServiceProviders/CreateMerchantServiceProvider.jsx';
import ViewMerchantServiceProvider from '../pages/merchantServiceProviders/ViewMerchantServiceProvider.jsx';
import EquipmentModelList from '../pages/equipmentModels/EquipmentModelList.jsx';
import CreateEquipmentModel from '../pages/equipmentModels/CreateEquipmentModel.jsx';
import ViewEquipmentModel from '../pages/equipmentModels/ViewEquipmentModel.jsx';
import ImportBusinessList from '../pages/importBusinesses/ImportBusinessList.jsx';
import ViewImportBusiness from '../pages/importBusinesses/ViewImportBusiness.jsx';
import {
  LEASE_LIST_ACCESS,
  LEASE_VIEW_ACCESS,
  LEASE_PERMISSIONS,
} from '../pages/leases/leasePermissions';
import {
  MERCHANT_SERVICE_PROVIDER_LIST_ACCESS,
  MERCHANT_SERVICE_PROVIDER_VIEW_ACCESS,
  MERCHANT_SERVICE_PROVIDER_PERMISSIONS,
} from '../pages/merchantServiceProviders/merchantServiceProviderPermissions';
import {
  EQUIPMENT_MODEL_LIST_ACCESS,
  EQUIPMENT_MODEL_VIEW_ACCESS,
  EQUIPMENT_MODEL_PERMISSIONS,
} from '../pages/equipmentModels/equipmentModelPermissions';
import {
  IMPORT_BUSINESS_LIST_ACCESS,
  IMPORT_BUSINESS_VIEW_ACCESS,
} from '../pages/importBusinesses/importBusinessPermissions';
import {
  IMPORT_BUSINESSES_LIST_PATH,
  IMPORT_BUSINESSES_VIEW_PATH,
} from './path';

/** Wraps a component with ProtectedRoute */
const Protected = ({ children, permission, permissions = [] }) => (
  <ProtectedRoute
    requiredPermission={permissions.length > 0 ? undefined : permission}
    requiredPermissions={permissions.length > 0 ? permissions : []}
  >
    {children}
  </ProtectedRoute>
  
);

const RedirectToDataView = () => {
  const { id } = useParams();
  return <Navigate to={`${IMPORT_BUSINESSES_VIEW_PATH}/${id}`} replace />;
};



/**
 * All application routes. Add new routes here to keep App.js lean.
 */
export const AppRoutes = () => (
  
  <Routes>
    <Route path="/login" element={<Login />} />
    <Route path="/register" element={<Register />} />
    <Route path="/" element={<Protected><Navigate to="/dashboard" replace /></Protected>} />

    <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
    <Route path="/profile" element={<Protected><ProfilePage /></Protected>} />
    <Route path="/notifications" element={<Protected><NotificationsPage /></Protected>} />

    {/* Merchant / DBA routes */}
    <Route path="/create-merchant" element={<Protected permission="dba-create"><CreateMerchant /></Protected>} />
    <Route path="/in-underwriting" element={<Protected permission="dba-list"><InUnderwriting /></Protected>} />

    <Route path="/merchants/accounts" element={<Protected permission="dba-list"><MerchantList /></Protected>} />
    <Route path="/merchants/equipments" element={<Protected permission="dba-list"><Equipemnts /></Protected>} />


    <Route path="/pending" element={<Protected permission="dba-pending-list"><Pending /></Protected>} />
    <Route path="/complete" element={<Protected permission="dba-completed-list"><Complete /></Protected>} />
    <Route path="/view-merchant" element={<Protected permission="dba-view"><ViewMerchant /></Protected>} />
    <Route path="/sales-dba" element={<Protected permission="dba-sales-list"><SalesDbaListing /></Protected>} />

    {/* User management */}
    <Route path="/create-user" element={<Protected permissions={['user-create', 'user-edit', 'user-view']}><UserCreatePage /></Protected>} />
    <Route path="/users-list" element={<Protected permissions={['user-list', 'user-direct-permission']}><UserListPage /></Protected>} />

    {/* Customer management */}
    <Route
      path="/customers"
      element={<Protected permissions={CUSTOMER_LIST_ACCESS}><CustomerList /></Protected>}
    />
    <Route
      path="/customers/create"
      element={<Protected permission={CUSTOMER_PERMISSIONS.CREATE}><CreateCustomer /></Protected>}
    />
    <Route
      path="/customers/edit/:id"
      element={<Protected permission={CUSTOMER_PERMISSIONS.EDIT}><CreateCustomer /></Protected>}
    />
    <Route
      path="/customers/view/:id"
      element={<Protected permissions={CUSTOMER_VIEW_ACCESS}><CreateCustomer /></Protected>}
    />

    {/* Team management */}
    <Route path="/teams-lists" element={<Protected permission="team-list"><TeamsListPage /></Protected>} />
    <Route path="/create-team" element={<Protected permission="team-create"><CreateTeamPage /></Protected>} />

    {/* Leads */}
    <Route path="/leads" element={<Protected permission="lead-list"><LeadList /></Protected>} />
    <Route path="/leads/create" element={<Protected permission="lead-create"><CreateLead /></Protected>} />
    <Route path="/leads/edit/:id" element={<Protected permission="lead-edit"><CreateLead /></Protected>} />
    <Route path="/leads/view/:id" element={<Protected permission="lead-view"><LeadDetail /></Protected>} />
    <Route
      path="/leads/:id/upload-documents"
      element={<Protected permission="attachment-upload"><LeadUploadDocuments /></Protected>}
    />

    {/* Call recordings */}
    <Route path="/call-records" element={<Protected permission="call-record-list"><CallRecordList /></Protected>} />
    <Route path="/call-records/create" element={<Protected permission="call-record-create"><CreateCallRecord /></Protected>} />
    <Route path="/call-records/view/:id" element={<Protected permission="call-record-view"><ViewCallRecord /></Protected>} />
    <Route path="/call-records/edit/:id" element={<Protected permission="call-record-edit"><EditCallRecord /></Protected>} />

    {/* Issues */}
    <Route path="/issues" element={<Protected permission="issue-list"><IssueList /></Protected>} />
    <Route path="/issues/view/:id" element={<Protected permission="issue-view"><IssueDetail /></Protected>} />

    {/* Permissions - either permission grants access */}
    <Route path="/permission/role" element={<Protected permission="role-view"><RolesManagement /></Protected>} />
    <Route path="/permissions/assign" element={<Protected permissions={['role-view', 'user-direct-permission']}><PermissionAssignment /></Protected>} />

    {/* Roles */}
    <Route
      path="/roles"
      element={<Protected permissions={LIST_ROUTE_ANY_PERMISSIONS['/roles']}><RoleList /></Protected>}
    />
    <Route path="/roles/create" element={<Protected permissions={['role-create', 'role-view']}><CreateRole /></Protected>} />
    <Route path="/roles/edit/:id" element={<Protected permissions={['role-edit', 'role-view']}><CreateRole /></Protected>} />

    {/* Statuses */}
    <Route
      path="/statuses"
      element={<Protected permissions={LIST_ROUTE_ANY_PERMISSIONS['/statuses']}><StatusList /></Protected>}
    />
    <Route
      path="/statuses/create"
      element={<Protected permissions={['status-create', 'status-view']}><CreateStatus /></Protected>}
    />

    {/* Contracts */}
    <Route path="/contracts" element={<Protected permission="eSignContract-list"><ContractList /></Protected>} />

    {/* Daily Summary / Agent Call Reports */}
    <Route path="/daily-summary" element={<Protected permission="agentCallReport-list"><DailySummaryList /></Protected>} />
    <Route path="/daily-summary/create" element={<Protected permission="agentCallReport-create"><CreateDailySummary /></Protected>} />
    <Route path="/daily-summary/edit/:id" element={<Protected permission="agentCallReport-edit"><EditDailySummary /></Protected>} />
    <Route path="/daily-summary/view/:id" element={<Protected permission="agentCallReport-view"><ViewDailySummary /></Protected>} />

    {/* Calls Counter Summary */}
    <Route path="/calls-counter-summary" element={<Protected><CallsCounterSummary /></Protected>} />
    {/* Leads Report */}
    <Route path="/leads-report" element={<Protected><LeadsReport /></Protected>} />
    <Route path="/reports/recent-activities" element={<Protected permission="log-activity-view"><RecentActivitiesReport /></Protected>} />

    {/* Other */}
    <Route path="/view-log/:id" element={<Protected permission="log-activity-view"><ViewLogsPage /></Protected>} />
    {/* Meetings */}
    <Route path="/meetings/calendar" element={<Protected permission="meeting-list"><MeetingCalendar /></Protected>} />
    <Route path="/meetings/create" element={<Protected permission="meeting-create"><CreateMeeting /></Protected>} />
    <Route path="/meetings/edit/:id" element={<Protected permission="meeting-edit"><EditMeeting /></Protected>} />
    <Route path="/meetings/view/:id" element={<Protected permission="meeting-view"><MeetingView /></Protected>} />




 {/* Brands */}
    <Route path="/brands/create" element={<Protected><CreateBrand /></Protected>} />
    <Route path="/brands/edit/:id" element={<Protected><CreateBrand /></Protected>} />
    <Route path="/brands/view/:id" element={<Protected><ViewBrand /></Protected>} />
    <Route path="/brands" element={<Protected><BrandList /></Protected>} />

    {/* Services */}
    <Route path="/services/create" element={<Protected permission="service-create"><CreateService /></Protected>} />
    <Route path="/services/edit/:id" element={<Protected permission="service-edit"><CreateService /></Protected>} />
    <Route path="/services/view/:id" element={<Protected permission="service-view"><ViewService /></Protected>} />
    <Route
      path="/services"
      element={<Protected permissions={LIST_ROUTE_ANY_PERMISSIONS['/services']}><ServicesList /></Protected>}
    />

    {/* Leases */}
    <Route
      path="/leases/create"
      element={<Protected permission={LEASE_PERMISSIONS.CREATE}><CreateLease /></Protected>}
    />
    <Route
      path="/leases/edit/:id"
      element={<Protected permission={LEASE_PERMISSIONS.EDIT}><CreateLease /></Protected>}
    />
    <Route
      path="/leases/view/:id"
      element={<Protected permissions={LEASE_VIEW_ACCESS}><ViewLease /></Protected>}
    />
    <Route
      path="/leases"
      element={<Protected permissions={LEASE_LIST_ACCESS}><LeaseList /></Protected>}
    />

    {/* Merchant service providers */}
    <Route
      path="/merchant-service-providers/create"
      element={
        <Protected permission={MERCHANT_SERVICE_PROVIDER_PERMISSIONS.CREATE}>
          <CreateMerchantServiceProvider />
        </Protected>
      }
    />
    <Route
      path="/merchant-service-providers/edit/:id"
      element={
        <Protected permission={MERCHANT_SERVICE_PROVIDER_PERMISSIONS.EDIT}>
          <CreateMerchantServiceProvider />
        </Protected>
      }
    />
    <Route
      path="/merchant-service-providers/view/:id"
      element={
        <Protected permissions={MERCHANT_SERVICE_PROVIDER_VIEW_ACCESS}>
          <ViewMerchantServiceProvider />
        </Protected>
      }
    />
    <Route
      path="/merchant-service-providers"
      element={
        <Protected permissions={MERCHANT_SERVICE_PROVIDER_LIST_ACCESS}>
          <MerchantServiceProviderList />
        </Protected>
      }
    />

    {/* Equipment models */}
    <Route
      path="/equipment-models/create"
      element={
        <Protected permission={EQUIPMENT_MODEL_PERMISSIONS.CREATE}>
          <CreateEquipmentModel />
        </Protected>
      }
    />
    <Route
      path="/equipment-models/edit/:id"
      element={
        <Protected permission={EQUIPMENT_MODEL_PERMISSIONS.EDIT}>
          <CreateEquipmentModel />
        </Protected>
      }
    />
    <Route
      path="/equipment-models/view/:id"
      element={
        <Protected permissions={EQUIPMENT_MODEL_VIEW_ACCESS}>
          <ViewEquipmentModel />
        </Protected>
      }
    />
    <Route
      path="/equipment-models"
      element={
        <Protected permissions={EQUIPMENT_MODEL_LIST_ACCESS}>
          <EquipmentModelList />
        </Protected>
      }
    />

    {/* Companies */}
    <Route path="/companies/create" element={<Protected permission="company-create"><CreateCompany /></Protected>} />
    <Route path="/companies/edit/:id" element={<Protected permission="company-edit"><CreateCompany /></Protected>} />
    <Route path="/companies/view/:id" element={<Protected permission="company-view"><ViewCompany /></Protected>} />
    <Route
      path="/companies"
      element={<Protected permissions={LIST_ROUTE_ANY_PERMISSIONS['/companies']}><CompanyList /></Protected>}
    />

    {/* Task types */}
    <Route path="/task-types/create" element={<Protected permission="task-type-create"><CreateTaskType /></Protected>} />
    <Route path="/task-types/edit/:id" element={<Protected permission="task-type-edit"><CreateTaskType /></Protected>} />
    <Route path="/task-types/view/:id" element={<Protected permission="task-type-view"><ViewTaskType /></Protected>} />
    <Route
      path="/task-types"
      element={<Protected permissions={LIST_ROUTE_ANY_PERMISSIONS['/task-types']}><TaskTypeList /></Protected>}
    />

    {/* Task categories */}
    <Route path="/task-categories/create" element={<Protected permission="task-category-create"><CreateTaskCategory /></Protected>} />
    <Route path="/task-categories/edit/:id" element={<Protected permission="task-category-edit"><CreateTaskCategory /></Protected>} />
    <Route path="/task-categories/view/:id" element={<Protected permission="task-category-view"><ViewTaskCategory /></Protected>} />
    <Route
      path="/task-categories"
      element={
        <Protected permissions={LIST_ROUTE_ANY_PERMISSIONS['/task-categories']}>
          <TaskCategoryList />
        </Protected>
      }
    />

    {/* Task templates */}
    <Route path="/task-templates/create" element={<Protected permission="task-template-create"><CreateTaskTemplate /></Protected>} />
    <Route path="/task-templates/edit/:id" element={<Protected permission="task-template-edit"><CreateTaskTemplate /></Protected>} />
    <Route path="/task-templates/view/:id" element={<Protected permission="task-template-view"><ViewTaskTemplate /></Protected>} />
    <Route
      path="/task-templates"
      element={
        <Protected permissions={LIST_ROUTE_ANY_PERMISSIONS['/task-templates']}>
          <TaskTemplateList />
        </Protected>
      }
    />

    {/* Tasks */}
    <Route path="/tasks/create" element={<Protected permission="task-create"><CreateTask /></Protected>} />
    <Route path="/tasks/edit/:id" element={<Protected permission="task-edit"><CreateTask /></Protected>} />
    <Route path="/tasks/view/:id" element={<Protected permission="task-view"><ViewTask /></Protected>} />
    <Route path="/tasks" element={<Protected permissions={LIST_ROUTE_ANY_PERMISSIONS['/tasks']}><TaskList /></Protected>} />





    {/* Data (import businesses) */}
    <Route
      path={IMPORT_BUSINESSES_LIST_PATH}
      element={
        <Protected permissions={IMPORT_BUSINESS_LIST_ACCESS}>
          <ImportBusinessList />
        </Protected>
      }
    />
    <Route
      path={`${IMPORT_BUSINESSES_VIEW_PATH}/:id`}
      element={
        <Protected permissions={IMPORT_BUSINESS_VIEW_ACCESS}>
          <ViewImportBusiness />
        </Protected>
      }
    />
    {/* Legacy redirects */}
    <Route path="/import-businesses" element={<Navigate to={IMPORT_BUSINESSES_LIST_PATH} replace />} />
    <Route path="/import-businesses/view/:id" element={<RedirectToDataView />} />
    <Route path="/import-data" element={<Navigate to={IMPORT_BUSINESSES_LIST_PATH} replace />} />
    <Route path="/import-data/view/:id" element={<RedirectToDataView />} />

    <Route path="*" element={<Navigate to="/dashboard" replace />} />
  </Routes>
);
